/**
 * @deprecated Legacy profile hooks. Use `src/features/profile/hooks/useProfile.ts` instead.
 *
 * These hooks use the old `useClient` pattern and have a broken `onMutate` optimistic update
 * that causes data loss. The new hooks in `features/profile` use the repository pattern
 * with proper cache management.
 *
 * Remaining consumers of hooks from this file:
 * - `useGetUserProfileStatus` — only `src/pages/dashboard/get-started.tsx` (deprecated page)
 * - `GET_USER_PROFILE_QUERY_KEY` — re-exported as `LEGACY_PROFILE_QUERY_KEY` in new hooks
 *
 * Types in `./types` are still shared — they're imported by both old and new code.
 */
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {useClient} from "../../utils/hooks";
import {AxiosError} from "axios";
import {
  GetUserProfileApiResponse,
  GetUserProfileV2ApiResponse,
  UpdateUserProfileApiPayload,
  UpdateUserProfileV2Payload,
} from "./types";
import {message} from "antd";
import {AnalyticsEvent} from "../../utils/analytics";

/**
 * Adapts the v2 GET profile response (nested `user` object, camelCase fields) to the
 * flat legacy `GetUserProfileApiResponse` shape (snake_case keys, flattened user fields)
 * that existing consumers of this module expect.
 *
 * This is a permanent shape-compatibility layer, not V1-dependent code — there is no v1
 * code path anymore. Migrating the remaining consumers off the flat legacy shape is a
 * separate, dedicated effort (see `src/context/profile/profile-context.tsx`, which has
 * 33+ consumers of this same shape).
 */
function normalizeV2ProfileResponse(
  v2: GetUserProfileV2ApiResponse
): GetUserProfileApiResponse {
  const {user} = v2;
  return {
    avatar: user.avatar,
    cover_image: user.coverImage,
    behance: v2.behance,
    buffer_time: v2.bufferTime,
    country: user.country,
    createdAt: new Date(v2.createdAt),
    description: v2.description,
    discord: v2.discord,
    display_name: v2.displayName,
    dribble: v2.dribble,
    email: user.email,
    facebook: v2.facebook,
    firstName: user.firstName,
    github: v2.github,
    how_did_you_find_us: v2.howDidYouFindUs,
    id: user.id,
    instagram: v2.instagram,
    isKyc_verified: false,
    isSocialAuth: user.isSocialAuth,
    is_wallet_frozen: false,
    lastName: user.lastName,
    linkedin: v2.linkedin,
    shortBio: v2.shortBio,
    statusId: user.statusId,
    statusName: "Active",
    suspension_reason: null,
    tiktok: v2.tiktok,
    twitch: v2.twitch,
    twitter: v2.twitter,
    updatedAt: new Date(v2.updatedAt),
    userId: v2.userId,
    username: v2.username,
    wallet_freeze_reason: null,
    website: v2.website,
    youtube: v2.youtube,
    twoFactorMethod: user.twoFactorMethod ?? null,
  };
}

function mapProfilePayloadToV2(
  payload: UpdateUserProfileApiPayload
): UpdateUserProfileV2Payload {
  return {
    firstName: payload.firstName,
    lastName: payload.lastName,
    displayName: payload.display_name,
    shortBio: payload.shortBio,
    country: payload.country,
    username: payload.username,
    description: payload.shortDescription,
    howDidYouFindUs: payload.how_did_you_find_us,
    facebook: payload.facebook,
    twitter: payload.twitter,
    instagram: payload.instagram,
    discord: payload.discord,
    youtube: payload.youtube,
    dribble: payload.dribble,
    twitch: payload.twitch,
    github: payload.github,
    website: payload.website,
    tiktok: payload.tiktok,
    linkedin: payload.linkedin,
    behance: payload.behance,
  };
}

export const GET_USER_PROFILE_QUERY_KEY = "user_profile";

export const useGetUserProfile = () => {
  const client = useClient("PROFILE");

  return useQuery<
    GetUserProfileApiResponse,
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: [GET_USER_PROFILE_QUERY_KEY],
    queryFn: async () => {
      const response = await client("profile");
      const inner = response.data;
      return normalizeV2ProfileResponse(inner as GetUserProfileV2ApiResponse);
    },
    // onError:(err: Error) => console.log({err})
    staleTime: Infinity,
  });
};

type ProfileStatusResponse = {
  profileStatus: "Active" | null;
  bankAccount: "Active" | null;
  serviceStatus: "Active" | null;
  availabilityStatus: "Active" | null;
};

/** v2 profile/status response: booleans, availabilitySchedule instead of availabilityStatus, no serviceStatus */
function normalizeProfileStatusV2(v2: {
  profile?: boolean;
  bankAccount?: boolean;
  availabilitySchedule?: boolean;
}): ProfileStatusResponse {
  return {
    profileStatus: v2.profile ? "Active" : null,
    bankAccount: v2.bankAccount ? "Active" : null,
    serviceStatus: null,
    availabilityStatus: v2.availabilitySchedule ? "Active" : null,
  };
}

/**
 * @deprecated Use `useProfileStatus` from `src/features/profile-status` instead.
 *
 * This legacy hook returns a flat `"Active" | null` shape. The replacement returns
 * a `ProfileStatus` entity with boolean flags and onboarding logic
 * (`isOnboardingComplete`, `incompleteSteps`, etc.).
 *
 * Remaining consumer: `src/pages/dashboard/get-started.tsx` (deprecated page,
 * separate cleanup). Do not add new callers.
 */
export const useGetUserProfileStatus = () => {
  const client = useClient("PROFILE");

  return useQuery<ProfileStatusResponse, AxiosError<{status: boolean; message: string}>>({
    queryKey: ["dashboard_status"],
    queryFn: async () => {
      const response = await client("profile/status");
      const data = response.data;
      return normalizeProfileStatusV2(data);
    },
    onError: (err) => {
      message.error(err.message);
      AnalyticsEvent("Dashboard Metrics", "Dashboard Metrics Fail");
    },
    onSuccess: () => {
      AnalyticsEvent("Dashboard Metrics", "Dashboard Metrics Success");
    },
  });
};

// const useGetProfile = () => {
//   const client = useClient();

//   return useQuery<TProfileApiResponse, AxiosError<{status: boolean; message: string}>>({
//     queryKey: [GET_USER_PROFILE_QUERY_KEY],
//     queryFn: () => client("profile").then((data) => data.data),
//     // onError:(err: Error) => console.log({err})
//     staleTime: Infinity,
//   });
// };

export const useUpdateUserProfile = () => {
  const queryClient = useQueryClient();
  const client = useClient("PROFILE");
  return useMutation({
    mutationFn: (profileData: UpdateUserProfileApiPayload) =>
      client("profile", {
        method: "POST",
        data: mapProfilePayloadToV2(profileData),
      }),
    onMutate: (values) => {
      queryClient.cancelQueries([GET_USER_PROFILE_QUERY_KEY]);
      const oldProfile: GetUserProfileApiResponse | undefined = queryClient.getQueryData([
        GET_USER_PROFILE_QUERY_KEY,
      ]);
      let newValues;
      if (oldProfile) {
        newValues = {
          avatar: oldProfile.avatar,
          behance: values.behance,
          country: values.country,
          cover_image: oldProfile.cover_image,
          createdAt: oldProfile.createdAt,
          description: values.shortDescription,
          discord: values.discord,
          dribble: values.dribble,
          email: oldProfile.email,
          facebook: values.facebook,
          firstName: values.firstName,
          github: values.github,
          id: oldProfile.id,
          instagram: values.instagram,
          isSocialAuth: oldProfile.isSocialAuth,
          lastName: values.lastName,
          linkedin: values.linkedin,
          shortBio: values.shortBio,
          statusId: oldProfile.statusId,
          statusName: oldProfile.statusName,
          tiktok: values.tiktok,
          twitch: values.twitch,
          twitter: values.twitter,
          updatedAt: oldProfile.updatedAt,
          userId: oldProfile.userId,
          username: values.username,
          website: values.website,
          youtube: values.youtube,
          buffer_time: oldProfile.buffer_time,
          display_name: values.display_name,
          how_did_you_find_us: values.how_did_you_find_us,
          is_wallet_frozen: oldProfile.is_wallet_frozen,
          isKyc_verified: oldProfile.isKyc_verified,
          suspension_reason: oldProfile.suspension_reason,
          wallet_freeze_reason: oldProfile.wallet_freeze_reason,
        };
      }

      queryClient.setQueryData([GET_USER_PROFILE_QUERY_KEY], newValues);
      return () => queryClient.setQueryData([GET_USER_PROFILE_QUERY_KEY], oldProfile);
    },
    onError: (error: AxiosError<{message: string}>, values, rollback) => {
      message.error(error.message);
      AnalyticsEvent("Coach profile", "Coach profile update fail");
      if (typeof rollback === "function") {
        rollback();
      }
    },
    onSuccess: (data, values) => {
      AnalyticsEvent("Coach profile", "Coach profile update success");
      queryClient.setQueryData([GET_USER_PROFILE_QUERY_KEY], data);
    },
    onSettled: (data, error, values) =>
      queryClient.invalidateQueries({queryKey: [GET_USER_PROFILE_QUERY_KEY]}),
  });
};

export const useVerifyUsername = (username: string) => {
  const client = useClient("PROFILE");
  const endpoint = `profile/username/validate/${encodeURIComponent(username)}`;

  return useQuery<
    {isAvailable?: boolean},
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: [GET_USER_PROFILE_QUERY_KEY, {username}],
    queryFn: async () => {
      const response = await client(endpoint);
      return response.data;
    },
    enabled: username.length >= 3,
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    // onError:(err: Error) => console.log({err})
    // staleTime: Infinity,
  });
};

interface DisplayNameErrorResponse {
  status: boolean;
  message: string;
}

export const useVerifyDisplayName = (
  displayName: string,
  options?: {enabled: boolean}
) => {
  const client = useClient("PROFILE");
  const endpoint = `profile/display-name/validate/${encodeURIComponent(displayName)}`;

  return useQuery<{isAvailable?: boolean}, AxiosError<DisplayNameErrorResponse>>({
    queryKey: ["display-name", displayName],
    queryFn: async () => {
      const response = await client(endpoint);
      return response.data;
    },
    enabled: !!displayName && displayName.length >= 3 && (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });
};
